import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';
import Select from 'react-select';

const styles = {
  control: (base, state) => ({
    ...base,
    'boxShadow': state.isFocused ? '0 0 0 1px #0079c1' : undefined,
    'border': state.isFocused ? 'none' : `1px solid ${base.borderColor}`,
    '&:hover': {
      border: state.isFocused ? 'none' : `1px solid ${base.borderColor}`
    }
  }),
  menu: base => ({
    ...base,
    marginTop: 0,
    boxShadow: '0 0 0 1px #0079c1',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0
  }),
  singleValue: base => ({
    ...base,
    padding: 5,
    borderRadius: 5
  }),
  valueContainer: base => ({
    ...base,
    padding: '0 8px'
  }),
  placeholder: base => ({
    ...base,
    paddingLeft: '4px',
    color: '#757575'
  }),
  dropdownIndicator: base => ({
    ...base,
    color: '#333'
  }),
  option: base => ({
    ...base,
    minHeight: 35
  }),
  input: base => ({
    ...base,
    'marginTop': '1rem',
    'padding': 0,
    '[type="text"]:focus': {
      boxShadow: 'none'
    }
  })
};

export default function CustomSelect({
  input,
  options,
  isSearchable = true,
  isClearable,
  ariaLabel,
  ...rest
}) {
  const val = options.find(opt => opt.value === input.value) || '';
  const { name, ...selectInput } = input;

  const selectRef = useRef(null);
  const hiddenInputRef = useRef(null);

  function noOptionsMessage() {
    return 'Loading...';
  }

  function handleChange(item) {
    // Community pattern for native/constraint validation with react-select:
    // clear customValidity as soon as the value changes (see JedWatson/react-select
    // #2149 / #4327). Do this synchronously — RFF validate can run before paint.
    if (hiddenInputRef.current) {
      hiddenInputRef.current.setCustomValidity('');
    }

    return item ? input.onChange(item.value) : input.onChange('');
  }

  const IndicatorSeparator = () => {
    return null;
  };

  function noop() {
    // no operation (do nothing real quick)
  }

  function focusSelect() {
    selectRef && selectRef.current.focus();
  }

  // Workaround a react-select issue where the element doesn't scroll
  // to the selected element when the input is gains focussed.
  const onMenuOpen = useCallback(() => {
    setTimeout(() => {
      const menuListRef = selectRef.current?.menuListRef;

      menuListRef
        ?.querySelector('.react-select__option--is-selected')
        ?.scrollIntoView({
          block: 'nearest',
          inline: 'start'
        });
    });
  }, []);

  // Clear stale HTML5 customValidity once final-form has a value. Validation
  // can run before React commits the controlled hidden input's DOM value.
  useEffect(() => {
    const hasValue =
      selectInput.value !== undefined &&
      selectInput.value !== null &&
      selectInput.value !== '';

    if (hiddenInputRef.current && hasValue) {
      hiddenInputRef.current.setCustomValidity('');
    }
  }, [selectInput.value]);

  // React select does not support native required on its searchable input
  // (JedWatson/react-select#4327). Adjacent hidden input is the established
  // workaround; v5.6+ has a built-in `required`+`name` API we could adopt later.
  //
  // Keep the value stringified so HTML5 constraint validation sees a stable DOM
  // value (same approach as DomValueSyncInput for numeric RFF values).
  const hiddenValue =
    selectInput.value === undefined || selectInput.value === null
      ? ''
      : String(selectInput.value);

  return (
    <>
      <Select
        {...selectInput}
        {...rest}
        aria-label={ariaLabel}
        ref={selectRef}
        options={options}
        value={val}
        onMenuOpen={onMenuOpen}
        classNamePrefix="react-select"
        components={{ IndicatorSeparator }}
        onChange={handleChange}
        noOptionsMessage={noOptionsMessage}
        isClearable={isClearable}
        isSearchable={isSearchable}
        styles={styles}
      />
      {!input.disabled && (
        <input
          ref={hiddenInputRef}
          name={name}
          aria-hidden="true"
          tabIndex={-1}
          onChange={noop}
          autoComplete="off"
          style={{
            opacity: 0,
            height: 0
          }}
          value={hiddenValue}
          onFocus={focusSelect}
          required={rest.required}
        />
      )}
    </>
  );
}

CustomSelect.propTypes = {
  input: PropTypes.object,
  options: PropTypes.array,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  ariaLabel: PropTypes.string
};
